/**
 * Очистка метаданных снимка перед отправкой (стори 13.11).
 *
 * <p>Пока все фото перекодировались, EXIF терялся сам собой. Режим «Оригинал»
 * (стори 13.6) этот побочный эффект отменил: файл уходит байт в байт, а вместе
 * с ним — координаты съёмки, модель камеры и серийный номер. Это регресс
 * приватности, который надо закрыть там же, где он появился.
 *
 * <p>Подход — **белый список, а не чёрный**. Мы не вычищаем «плохие» теги из
 * чужой структуры (там всегда найдётся ещё один MakerNote), а выбрасываем все
 * метаданные и собираем новый минимальный EXIF из двух полей, которые нужны
 * для правильного показа: ориентация и дата съёмки. Всё неизвестное отсекается
 * по умолчанию — для приватности это единственное безопасное направление.
 *
 * <p>Область действия — JPEG. WEBP-выход нашего кодировщика метаданных не
 * несёт, а PNG с геометкой в природе почти не встречается.
 */

const MARKER_SOI = 0xd8;
const MARKER_EOI = 0xd9;
const MARKER_SOS = 0xda;
const MARKER_APP0 = 0xe0;
const MARKER_APP1 = 0xe1;
const MARKER_APP15 = 0xef;
const MARKER_COM = 0xfe;

const TAG_ORIENTATION = 0x0112;
const TAG_DATETIME = 0x0132;
const TAG_EXIF_IFD_POINTER = 0x8769;
const TAG_DATETIME_ORIGINAL = 0x9003;

/** Поля, которые переживают очистку. Всё остальное не переживает. */
type KeptImageMetadata = {
  /** EXIF-ориентация 1..8. Без неё снимок с телефона ляжет на бок. */
  orientation?: number;
  /** «YYYY:MM:DD HH:MM:SS» — дата съёмки, а не файла. */
  dateTimeOriginal?: string;
};

function startsWithAscii(bytes: Uint8Array, at: number, s: string): boolean {
  if (at + s.length > bytes.length) return false;
  for (let i = 0; i < s.length; i += 1) {
    if (bytes[at + i] !== s.charCodeAt(i)) return false;
  }
  return true;
}

function u16(bytes: Uint8Array, at: number, little: boolean): number {
  return little ? bytes[at] | (bytes[at + 1] << 8) : (bytes[at] << 8) | bytes[at + 1];
}

function u32(bytes: Uint8Array, at: number, little: boolean): number {
  return little
    ? (bytes[at] | (bytes[at + 1] << 8) | (bytes[at + 2] << 16) | (bytes[at + 3] << 24)) >>> 0
    : ((bytes[at] << 24) | (bytes[at + 1] << 16) | (bytes[at + 2] << 8) | bytes[at + 3]) >>> 0;
}

/**
 * Читает из EXIF только то, что мы собираемся сохранить. Чтение строго
 * в границах массива: файл приходит от пикера, но структура в нём может быть
 * какой угодно.
 */
export function readKeptMetadataFromExif(exif: Uint8Array): KeptImageMetadata {
  const out: KeptImageMetadata = {};
  // exif указывает на начало TIFF-заголовка (после "Exif\0\0").
  if (exif.length < 8) return out;
  const byteOrder = u16(exif, 0, false);
  const little = byteOrder === 0x4949;
  if (!little && byteOrder !== 0x4d4d) return out;
  if (u16(exif, 2, little) !== 0x002a) return out;
  const ifd0 = u32(exif, 4, little);
  if (ifd0 + 2 > exif.length) return out;

  const readIfd = (offset: number, visit: (tag: number, type: number, count: number, valueAt: number) => void) => {
    if (offset + 2 > exif.length) return;
    const count = u16(exif, offset, little);
    // 12 байт на запись; заведомо битый счётчик просто игнорируем.
    if (count > 4096 || offset + 2 + count * 12 > exif.length) return;
    for (let i = 0; i < count; i += 1) {
      const at = offset + 2 + i * 12;
      visit(u16(exif, at, little), u16(exif, at + 2, little), u32(exif, at + 4, little), at + 8);
    }
  };

  const readAscii = (count: number, valueAt: number): string | undefined => {
    const at = count > 4 ? u32(exif, valueAt, little) : valueAt;
    if (at + count > exif.length) return undefined;
    let s = '';
    for (let i = 0; i < count; i += 1) {
      const c = exif[at + i];
      if (c === 0) break;
      s += String.fromCharCode(c);
    }
    return s.length > 0 ? s : undefined;
  };

  let exifIfd = 0;
  let fallbackDateTime: string | undefined;
  readIfd(ifd0, (tag, type, count, valueAt) => {
    if (tag === TAG_ORIENTATION && type === 3) {
      const v = u16(exif, valueAt, little);
      if (v >= 1 && v <= 8) out.orientation = v;
    } else if (tag === TAG_EXIF_IFD_POINTER) {
      exifIfd = u32(exif, valueAt, little);
    } else if (tag === TAG_DATETIME && type === 2) {
      fallbackDateTime = readAscii(count, valueAt);
    }
  });
  if (exifIfd > 0) {
    readIfd(exifIfd, (tag, type, count, valueAt) => {
      if (tag === TAG_DATETIME_ORIGINAL && type === 2) {
        out.dateTimeOriginal = readAscii(count, valueAt);
      }
    });
  }
  if (!out.dateTimeOriginal && fallbackDateTime) out.dateTimeOriginal = fallbackDateTime;
  return out;
}

/**
 * Собирает минимальный APP1-сегмент с сохранёнными полями. Возвращает `null`,
 * если сохранять нечего — тогда снимок уходит вовсе без EXIF.
 */
export function buildMinimalExifApp1(meta: KeptImageMetadata): Uint8Array | null {
  const date = meta.dateTimeOriginal?.trim();
  const hasDate = typeof date === 'string' && date.length > 0;
  const hasOrientation = typeof meta.orientation === 'number';
  if (!hasDate && !hasOrientation) return null;

  const dateBytes = hasDate ? Math.min(date.length, 63) + 1 : 0; // + завершающий 0
  const ifd0Count = (hasOrientation ? 1 : 0) + (hasDate ? 1 : 0);
  const ifd0At = 8;
  const ifd0Size = 2 + ifd0Count * 12 + 4;
  const exifIfdAt = ifd0At + ifd0Size;
  const exifIfdSize = hasDate ? 2 + 12 + 4 : 0;
  const dateAt = exifIfdAt + exifIfdSize;
  const tiffSize = dateAt + dateBytes;

  const out = new Uint8Array(2 + 2 + 6 + tiffSize);
  let p = 0;
  const put8 = (v: number) => {
    out[p] = v & 0xff;
    p += 1;
  };
  const put16 = (v: number) => {
    out[p] = (v >> 8) & 0xff;
    out[p + 1] = v & 0xff;
    p += 2;
  };
  const put32 = (v: number) => {
    out[p] = (v >>> 24) & 0xff;
    out[p + 1] = (v >>> 16) & 0xff;
    out[p + 2] = (v >>> 8) & 0xff;
    out[p + 3] = v & 0xff;
    p += 4;
  };

  put8(0xff);
  put8(MARKER_APP1);
  put16(2 + 6 + tiffSize); // длина сегмента включает сами два байта длины
  for (const ch of 'Exif') put8(ch.charCodeAt(0));
  put8(0);
  put8(0);

  // TIFF, big-endian: свои байты пишем сами, разночтений с чужим порядком нет.
  put16(0x4d4d);
  put16(0x002a);
  put32(ifd0At);

  put16(ifd0Count);
  if (hasOrientation) {
    put16(TAG_ORIENTATION);
    put16(3); // SHORT
    put32(1);
    put16(meta.orientation as number);
    put16(0); // SHORT занимает 2 байта из 4 отведённых
  }
  if (hasDate) {
    put16(TAG_EXIF_IFD_POINTER);
    put16(4); // LONG
    put32(1);
    put32(exifIfdAt);
  }
  put32(0); // следующего IFD нет

  if (hasDate) {
    put16(1);
    put16(TAG_DATETIME_ORIGINAL);
    put16(2); // ASCII
    put32(dateBytes);
    put32(dateAt);
    put32(0);
    const text = (date as string).slice(0, 63);
    for (let i = 0; i < text.length; i += 1) put8(text.charCodeAt(i));
    put8(0);
  }
  return out;
}

type JpegSanitizeResult = {
  bytes: Uint8Array;
  /** `false` — в файле нечего было убирать, копию делать незачем. */
  changed: boolean;
  /** Что удалось сохранить: полезно для теста и для отладки на устройстве. */
  kept: KeptImageMetadata;
};

/**
 * Выбрасывает из JPEG все метаданные, кроме ориентации, даты съёмки, JFIF
 * и цветового профиля ICC. Пиксели не трогает: «Оригинал» остаётся оригиналом.
 */
export function sanitizeJpegMetadata(input: Uint8Array): JpegSanitizeResult {
  const empty: KeptImageMetadata = {};
  if (input.length < 4 || input[0] !== 0xff || input[1] !== MARKER_SOI) {
    return { bytes: input, changed: false, kept: empty };
  }

  const keep: Array<{ from: number; to: number }> = [];
  let kept: KeptImageMetadata = empty;
  let dropped = false;
  let pos = 2;
  let tailFrom = input.length;

  while (pos + 3 < input.length) {
    if (input[pos] !== 0xff) {
      // Рассинхронизация: дальше не наша забота, копируем как есть.
      tailFrom = pos;
      break;
    }
    const marker = input[pos + 1];
    if (marker === MARKER_SOS || marker === MARKER_EOI) {
      tailFrom = pos;
      break;
    }
    const size = (input[pos + 2] << 8) | input[pos + 3];
    if (size < 2 || pos + 2 + size > input.length) {
      tailFrom = pos;
      break;
    }
    const from = pos;
    const to = pos + 2 + size;
    const payloadAt = pos + 4;
    const isApp = marker >= MARKER_APP0 && marker <= MARKER_APP15;

    if (marker === MARKER_APP1 && startsWithAscii(input, payloadAt, 'Exif\0\0')) {
      kept = readKeptMetadataFromExif(input.subarray(payloadAt + 6, to));
      dropped = true;
    } else if (marker === MARKER_APP0 && startsWithAscii(input, payloadAt, 'JFIF\0')) {
      keep.push({ from, to });
    } else if (marker === MARKER_APP0 && startsWithAscii(input, payloadAt, 'JFXX\0')) {
      keep.push({ from, to });
    } else if (startsWithAscii(input, payloadAt, 'ICC_PROFILE\0')) {
      // Цветовой профиль — не метаданные о съёмке, без него поедут цвета.
      keep.push({ from, to });
    } else if (isApp || marker === MARKER_COM) {
      // XMP, IPTC, MakerNote, комментарии — всё за борт.
      dropped = true;
    } else {
      keep.push({ from, to });
    }
    pos = to;
  }

  if (!dropped) {
    return { bytes: input, changed: false, kept };
  }

  const rebuiltExif = buildMinimalExifApp1(kept);
  let total = 2 + (rebuiltExif ? rebuiltExif.length : 0) + (input.length - tailFrom);
  for (const seg of keep) total += seg.to - seg.from;

  const out = new Uint8Array(total);
  let w = 0;
  out[w++] = 0xff;
  out[w++] = MARKER_SOI;
  // JFIF по стандарту идёт сразу за SOI, поэтому свой EXIF ставим после него.
  let exifWritten = false;
  const writeExif = () => {
    if (rebuiltExif && !exifWritten) {
      out.set(rebuiltExif, w);
      w += rebuiltExif.length;
      exifWritten = true;
    }
  };
  for (const seg of keep) {
    const isJfif =
      input[seg.from + 1] === MARKER_APP0 && startsWithAscii(input, seg.from + 4, 'JFIF\0');
    if (!isJfif) writeExif();
    out.set(input.subarray(seg.from, seg.to), w);
    w += seg.to - seg.from;
    if (isJfif) writeExif();
  }
  writeExif();
  out.set(input.subarray(tailFrom), w);
  return { bytes: out, changed: true, kept };
}
